import { Badge } from 'components/Badge/Badge'
import { DotText } from 'components/Badge/DotText'
import { Select } from 'components/Select/Select'
import { Pagination } from 'components/Table/Pagination'
import { Align, OptionsProps, Table } from 'components/Table/Table'
import {
  PROCUREMENT_PRIORITY_META,
  PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META,
} from 'constants/meta'
import { Box, Flex } from 'styling/GlobalStyles'
import { BigText, ColorVariant, NormalText } from 'styling/TypographyV2'
import {
  ProcurementPurchaseRequestListResponse,
  ProcurementRequestClientStatus,
} from 'types/procurement'

import { useQuery } from '@tanstack/react-query'
import { getProcurementPurchaseRequests } from 'api'
import { DepartmentSelect } from 'components/Select/Department.select'
import { useFilter } from 'hooks/useFilter'
import { convertToDate, timeAgo, toCsv } from 'utils/helpers'
import { useNavigate } from 'react-router-dom'

type FilterValues = {
  departmentId: string | number | null
  departmentName: string | null
  clientStatus: string | null
}

export const PurchaseRequest = () => {
  const { setMultipleValues, values, clearMultipleValues } = useFilter<FilterValues>({
    departmentId: null,
    departmentName: null,
    clientStatus: null,
  })
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['procurement-purchase-requests', values],
    queryFn: () =>
      getProcurementPurchaseRequests({
        departmentIds: values.departmentId ? toCsv([values.departmentId]) : undefined,
        clientStatuses: values.clientStatus ? toCsv([values.clientStatus]) : undefined,
        page: values.page,
        limit: values.limit,
      }),
  })

  const options: OptionsProps<ProcurementPurchaseRequestListResponse>[] = [
    {
      header: '#PR NUMBER',
      cell: (item) => (
        <NormalText>
          {'#PR-' + new Date(convertToDate(item.createdAt)).getFullYear() + '-' + item.id}
        </NormalText>
      ),
    },
    {
      header: 'TITLE',
      cell: (item) => (
        <div>
          <NormalText>{item.title}</NormalText>
          <NormalText colorVariant={ColorVariant.SECONDARY}>
            {item.requesterUser?.firstName + ' ' + item.requesterUser?.lastName}
          </NormalText>
        </div>
      ),
    },
    { header: 'DEPARTMENT', cell: (item) => item.department.name },
    {
      header: 'COST CENTER',
      cell: (item) => (
        <NormalText colorVariant={ColorVariant.SECONDARY}>{item.costCenter?.name}</NormalText>
      ),
    },
    {
      header: 'PRIORITY',
      cell: (item) => (
        <DotText hexColor={PROCUREMENT_PRIORITY_META[item.priority].color}>
          {PROCUREMENT_PRIORITY_META[item.priority].label}
        </DotText>
      ),
    },
    {
      header: 'STATUS',
      cell: (item) => {
        const statusMeta = PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META[item.clientStatus]
        if (!statusMeta) return null
        return <Badge hexColor={statusMeta.color}>{statusMeta.label}</Badge>
      },
    },
    {
      header: 'CURRENT STEP',
      cell: (item) => (
        <NormalText colorVariant={ColorVariant.SECONDARY}>{item.clientCurrentStep}</NormalText>
      ),
    },
    {
      header: 'AMOUNT',
      cell: (item) => (
        <BigText bold>{item.items.reduce((acc, item) => acc + item.quantity, 0)}</BigText>
      ),
      align: Align.RIGHT,
    },
    {
      header: 'AGE',
      cell: (item) => (
        <NormalText colorVariant={ColorVariant.SECONDARY}>{timeAgo(item.createdAt)}</NormalText>
      ),
      align: Align.RIGHT,
    },
  ]

  return (
    <>
      <Box height='60px' mb='16px'>
        <Flex gap='16px' height='100%' center justend>
          <DepartmentSelect
            width='200px'
            onChange={(value) => {
              if (!value?.value) {
                clearMultipleValues(['departmentId', 'departmentName'])
                return
              }
              setMultipleValues({ departmentId: value.value, departmentName: value.label })
            }}
            value={
              values.departmentId
                ? { label: values.departmentName, value: values.departmentId }
                : null
            }
          />
          <Select
            width='200px'
            nullValue={{ label: 'All Statuses', value: null }}
            options={Object.values(ProcurementRequestClientStatus)
              .filter((status) => !isNaN(Number(status)))
              .map((status) => ({
                label: PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META[status]?.label,
                value: status.toString(),
              }))}
            value={
              values.clientStatus
                ? {
                    label:
                      PROCUREMENT_REQUEST_CLIENT_STATUS_DISPLAY_META[values.clientStatus]?.label,
                    value: values.clientStatus.toString(),
                  }
                : null
            }
            onChange={(value) => {
              if (value.value === null) {
                clearMultipleValues(['clientStatus'])
                return
              }
              setMultipleValues({ clientStatus: value.value as string })
            }}
          />
        </Flex>
      </Box>
      <Table
        options={options}
        items={data?.data || []}
        cellKey='id'
        emptyListOptions={{
          description: 'No purchase requests match your filters.',
        }}
        loading={isLoading}
        onClick={(item) => {
          navigate(`/procurement/${item.id}`)
        }}
      />
      <Flex justend width='100%' mt='16'>
        <Pagination itemCount={data?.totalCount || 0} limit={values.limit || 20} />
      </Flex>
    </>
  )
}
